import { useTheme } from "@emotion/react";
import {
  Check,
  Close,
  ExpandLess,
  ExpandMore,
  MoreVert,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Slide,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { forwardRef, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import SelectWorkStationType from "./SelectWorkStationType";
import Tiptap from "../../../components/Tiptap/Tiptap";

const UpdateWorkStationDialog = ({
  open,
  onClose,
  onUpdated,
  onDeleted,
  onActivated,
  workStation,
}) => {
  const theme = useTheme();
  const { authToken, ezorder } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const updateWorkStation = async (values) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await ezorder.put(
        `/work-stations/${workStation.id}`,
        {
          name: values.name,
          workStationType: values.workStationType,

          workorderInstructionsText: values.workorderInstructionsText,
          workorderInstructionsJsonString:
            values.workorderInstructionsJsonString,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.status == 200 || response.status === 201) {
        onUpdated();
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const softDeleteWorkStation = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await ezorder.delete(
        `/work-stations/${workStation.id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.status == 200 || response.status === 201) {
        onDeleted();
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const reactivateWorkStation = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await ezorder.put(
        `/work-stations/${workStation.id}/activate`,
        {
          active: true,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.status == 200 || response.status === 201) {
        onActivated();
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const formValidationSchema = Yup.object({
    name: Yup.string().required("Name required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // name: workStation ? workStation.name : "",
      // workStationType: workStation ? workStation.workStationType : "",
      // workorderInstructionsText: workStation
      //   ? workStation.workorderInstructionsText
      //   : "",
      // workorderInstructionsJsonString: workStation
      //   ? workStation.workorderInstructionsJsonString
      //   : "",
      name: workStation ? workStation.name : "",
      workStationType: workStation ? workStation.workStationType : "",
      workorderInstructionsText: workStation
        ? workStation.workorderInstructionsText
        : undefined,
      workorderInstructionsJsonString: workStation
        ? workStation.workorderInstructionsJsonString
        : undefined,
    },
    validationSchema: formValidationSchema,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: (values) => {
      updateWorkStation(values);
    },
  });

  useEffect(() => {
    console.log("FORMIK VALUES", formik.values);
  }, [formik.values]);

  if (workStation && open && formik && formik.values)
    return (
      <Dialog
        fullScreen={false}
        fullWidth={true}
        open={open}
        onClose={() => {
          if (!loading) handleClose(); // do not allow close when loading
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>Update WorkStation #{workStation.id}</Box>
          <IconButton
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <MoreVert />
          </IconButton>
          <Menu open={menuOpen} onClose={handleMenuClose} anchorEl={anchorEl}>
            {workStation.deletedAt ? (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  reactivateWorkStation();
                }}
                disableRipple
              >
                <Check />
                Re-activate
              </MenuItem>
            ) : (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  softDeleteWorkStation();
                }}
                disableRipple
              >
                <Close />
                Deactivate
              </MenuItem>
            )}
          </Menu>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            sx={{ marginTop: "8px" }}
            label="Name"
            id="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.errors.name && `${formik.errors.name}`}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <SelectWorkStationType
            size="small"
            fullWidth
            sx={{ marginTop: "8px" }}
            label="Type"
            id="workStationType"
            name="workStationType"
            value={formik.values.workStationType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.workStationType &&
              Boolean(formik.errors.workStationType)
            }
            helperText={
              formik.errors.workStationType &&
              `${formik.errors.workStationType}`
            }
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <Typography sx={{ marginTop: "24px", fontWeight: "bold" }}>
            Default CFI Work Order Instructions
          </Typography>
          {workStation &&
            // Formik might initialize the data as undefined.
            // It get s replaced by null or a string when it loads the data from the workStation which is never undefined
            formik.values.workorderInstructionsJsonString !== undefined && (
              <Tiptap
                editorContent={
                  workStation && formik.values.workorderInstructionsJsonString
                    ? JSON.parse(formik.values.workorderInstructionsJsonString)
                    : ""
                }
                onChange={(htmlValue, jsonValue, textValue) => {
                  formik.setValues((prev) => ({
                    ...prev,
                    workorderInstructionsJsonString: JSON.stringify(jsonValue),
                    workorderInstructionsText: textValue,
                  }));
                }}
              />
            )}

          <Box>
            {errorMessage && (
              <Box>
                <Typography color={"red"}>{errorMessage}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={loading}
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            loading={loading}
            onClick={() => {
              formik.submitForm();
            }}
          >
            Update
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
};

export default UpdateWorkStationDialog;
